<template>
  <div v-if="user" class="war-room-comment">
    <header class="war-room-comment__header">
      <span class="war-room-comment__author war-room-comment-author">
        <div class="war-room-comment-author__avatar avatar avatar--non-empty avatar--next-tiny">
          <svg width="100%" height="100%" viewBox="0 0 32 32" fill="none" class="avatar__img">
            <rect width="32" height="32" fill="#E6E6E6"></rect>
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M16 18C19.3137 18 22 15.3137 22 12C22 8.68629 19.3137 6 16 6C12.6863 6 10 8.68629 10 12C10 15.3137 12.6863 18 16 18ZM27 32V26C27 22.6863 24.3137 20 21 20H11C7.68629 20 5 22.6863 5 26V32H27Z"
              fill="#979797"
            />
          </svg>
        </div>
        <div class="war-room-comment-author__info">
          <div class="war-room-comment-author__name">{{ user.username }}</div>
          <div class="war-room-comment-author__achievements">
            <div
              class="war-room-comment-author__achievement war-room-achievement war-room-achievement--platinum war-room-achievement--small"
            >
              <div class="war-room-achievement__border-overlay"></div>
              <div class="war-room-achievement__container">
                <div v-if="achievement" class="war-room-achievement__text">
                  {{ achievement }}
                </div>
              </div>
            </div>
            <div
              class="war-room-comment-author__achievement war-room-achievement war-room-achievement--no_level war-room-achievement--small"
            >
              <div class="war-room-achievement__border-overlay"></div>
              <div class="war-room-achievement__container">
                <div class="war-room-achievement__text">{{ comment.rating }}</div>
              </div>
            </div>
          </div>
          <div class="war-room-comment-author__position">
            {{ user.position }}
          </div>
        </div>
      </span>
      <div class="dropdown">
        <div class="dropdown__toggler">
          <div data-test="actions-toggle" class="dropdown-toggler war-room-comment__actions-toogler">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="war-room-comment__actions-icon"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M14 5a2 2 0 11-4 0 2 2 0 014 0zm0 7a2 2 0 11-4 0 2 2 0 014 0zm-2 9a2 2 0 100-4 2 2 0 000 4z"
                fill="currentColor"
              ></path>
            </svg>
          </div>
        </div>
      </div>
    </header>
    <div class="war-room-comment__content">
      <span data-test="war_room_comment_text" class="war-room-comment__text" v-html="comment.text" />
    </div>
    <div v-if="comment.replies.length">
      <WarRoomCommentPlaceholder v-for="c in comment.replies" :key="c.id" :comment="c" :post="post" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, useStore } from '@nuxtjs/composition-api';
import type { WarRoom } from '@/types/store/warRoom';
import type { RootState } from '~/types/store';

type WarRoomCommentPlaceholderProps = {
  comment?: WarRoom.State['commentTree'][number];
  post: WarRoom.CurrentPost;
};

const props = withDefaults(defineProps<WarRoomCommentPlaceholderProps>(), {
  comment: () => ({}),
});
const store = useStore<RootState>();

const userDict = computed(() => store.state.warRoom.user_dict);
const publicUserDict = computed(() => store.state.warRoom.public_user_dict);

// In some cases we don't have username, quickfix is to hide comment if no username
// https://sentry.io/organizations/bravado-network/issues/3884962220/?environment=production&project=5952605&query=is%3Aunresolved&referrer=issue-stream
const user = computed(
  () => userDict.value[props.comment.user_username] || publicUserDict.value[props.comment.user_slug],
);
const achievement = computed(() => user.value?.achievements?.[0]?.title);
</script>

<style scoped lang="scss">
a {
  background-color: transparent;
  color: inherit;
}

.war-room-comment {
  margin-top: 32px;
}

.dropdown,
.dropdown__toggler {
  position: relative;
}

.dropdown-toggler {
  cursor: pointer;
}

.avatar {
  border-radius: 50%;
  color: var(--gray-50);
  position: relative;
  height: 20px;
  width: 20px;

  svg {
    border-radius: 50%;
  }
}

.avatar--non-empty {
  background-color: initial;
  background-image: none;
  border: 1px solid rgba(121, 123, 134, 0.2);
}

.avatar__img-ring {
  border: 1px solid var(--gray-50);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  bottom: 0;
  left: 0;
  opacity: 0.1;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
}

.war-room-achievement {
  height: max-content;
  overflow: hidden;
  padding: 1px;
  position: relative;
}

.war-room-achievement__border-overlay {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
}

.war-room-achievement__container {
  z-index: 2;
}

.war-room-achievement__text {
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  line-height: inherit;
  white-space: nowrap;
}

.war-room-achievement--small {
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.33;
}

.war-room-achievement--platinum {
  background-color: initial;
  background-image: conic-gradient(
    from 171.52deg at 50% 50%,
    #f0f1f6 0deg,
    #a0b9cc 90deg,
    #dde5f0 180deg,
    #c5e6f9 234.26deg,
    #a7b5bb 270deg,
    #ecf1f4 1turn
  );
}

.war-room-achievement--no_level {
  border: 1px solid #d6d8e1;
  border-image: none 100% 1 0 stretch;
  color: var(--gray-50);
}

.war-room-comment-author {
  align-items: center;
  display: flex;
  position: relative;
  text-decoration: none;
}

.war-room-comment-author__avatar {
  margin-right: 10px;
}

.war-room-comment-author__info {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
}

.war-room-comment-author__name {
  font-size: 13px;
  font-weight: 600;
  line-height: 24px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  flex-shrink: 1;
  color: var(--theme-color-text);
  margin: 0 6px 0 0;
}

.war-room-comment-author__position {
  color: var(--gray-50);
  display: none;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5384;
  margin-left: 14px;
  position: relative;
}

@media only screen and (min-width: 768px) {
  .war-room-comment-author__position {
    display: block;
  }
}

.war-room-comment-author__achievements {
  align-items: center;
  display: flex;
}

.war-room-comment-author__achievement {
  margin-right: 4px;
}

.war-room-comment__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.war-room-comment__author {
  flex-grow: 1;
  margin-right: 10px;
}

.war-room-comment__text {
  font-size: 15px;
  line-height: 1.6;
  font-weight: 400;
  word-break: break-word;
  word-wrap: break-word;
  color: var(--theme-color-text);
  margin-bottom: 4px;
}

.war-room-comment__actions-toogler {
  line-height: 0;
}

.war-room-comment__actions-icon {
  color: #979797;
  height: 16px;
  width: 16px;
}

.war-room-comment__content {
  overflow: hidden;
}

.avatar > img {
  min-height: 100%;
  min-width: 100%;
}

.war-room-comment-author__position::before {
  background-color: var(--gray-50);
  border-radius: 50%;
  content: '';
  height: 2px;
  left: -8px;
  margin-top: -1px;
  position: absolute;
  top: 50%;
  width: 2px;
}

.war-room-achievement--small .war-room-achievement__container {
  padding: 1px 5px;
}

.war-room-achievement--platinum .war-room-achievement__text {
  -webkit-text-fill-color: transparent;
  background-clip: text;
  background-color: initial;
  background-image: linear-gradient(rgba(7, 15, 20, 0.9), rgba(92, 101, 106, 0.9));
  color: rgba(0, 0, 0, 0.7);
}

.war-room-achievement--platinum .war-room-achievement__border-overlay {
  background-color: initial;
  background-image: linear-gradient(rgba(42, 17, 78, 0.15), rgba(25, 10, 48, 0.15));
}

.war-room-comment-author__achievement:last-child {
  margin-right: 0;
}

@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
